import type { GetStaticProps, NextPage } from 'next';
import Image from 'next/image';
import Head from 'next/head';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import MainImage from './../public/images/main.png';
import Trustpilot from './../public/images/trustpilot.jpg';
import Trustpilot2 from './../public/images/trustpilot2.jpg';
import Trustpilot3 from './../public/images/trustpilot3.jpg';
import BrgsIcon from './../public/images/brgs.png';
import IfsIcon from './../public/images/ifs.png';
import FscIcon from './../public/images/fsc.png';
import VeganIcon from './../public/images/vegan.png';

import WhySundose1 from './../public/images/why1.jpg';
import WhySundose2 from './../public/images/why2.png';

import NeedsIcon1 from './../public/images/icons1/1.svg';
import NeedsIcon2 from './../public/images/icons1/2.svg';
import NeedsIcon3 from './../public/images/icons1/3.svg';
import NeedsIcon4 from './../public/images/icons1/4.svg';
import NeedsIcon5 from './../public/images/icons1/5.svg';
import NeedsIcon6 from './../public/images/icons1/6.svg';

import FittedIcon1 from './../public/images/icons2/1.svg';
import FittedIcon2 from './../public/images/icons2/2.svg';
import FittedIcon3 from './../public/images/icons2/3.svg';
import FittedIcon4 from './../public/images/icons2/4.svg';
import FittedIcon5 from './../public/images/icons2/5.svg';
import FittedIcon6 from './../public/images/icons2/6.svg';

import BestFormIcon1 from './../public/images/icons3/1.svg';
import BestFormIcon2 from './../public/images/icons3/2.svg';
import BestFormIcon3 from './../public/images/icons3/3.svg';
import BestFormIcon4 from './../public/images/icons3/4.svg';

import InfoIcon1 from './../public/images/icons4/1.svg';
import InfoIcon2 from './../public/images/icons4/2.svg';
import InfoIcon3 from './../public/images/icons4/3.svg';
import InfoIcon4 from './../public/images/icons4/4.svg';
import InfoIcon5 from './../public/images/icons4/5.svg';
import InfoIcon6 from './../public/images/icons4/6.svg';
import InfoIcon7 from './../public/images/icons4/7.svg';

import Adam from './../public/images/adam.jpg';
import Adrian from './../public/images/adrian.jpg';
import Agnieszka from './../public/images/agnieszka.jpg';
import Monika from './../public/images/monika.jpg';

import MagnesiumImage from './../public/images/Magnez.jpg';
import LiverImage from './../public/images/Watroba.jpg';
import MineralsImage from './../public/images/Mineraly.jpg';
import VitDImage from './../public/images/VitD.jpg';
import OmegaImage from './../public/images/Omega.jpg';

import SurveyProgressIcon from './../public/images/surveyprogress.svg';

import styles from './../styles/Index.module.scss';

import WomanIcon from './../public/images/woman.svg';
import ManIcon from './../public/images/man.svg';
import CtaButton from '../components/CtaButton';
import { useRouter } from 'next/router';
import { pushToDataLayer } from '../utils';
import React from 'react';

const Index: NextPage = () => {
  const { t } = useTranslation('index');

  const reviews = [
    {
      icon: WomanIcon,
      name: t('review-person-1'),
      text: t('review-description-1')
    },
    {
      icon: ManIcon,
      name: t('review-person-2'),
      text: t('review-description-2')
    },
    {
      icon: WomanIcon,
      name: t('review-person-3'),
      text: t('review-description-3')
    }
  ];

  const ingredients = [
    {
      name: t('ingredients-group-name-1'),
      image: MagnesiumImage,
      items: [
        t('ingredients-group-item-1'),
        t('ingredients-group-item-2'),
        t('ingredients-group-item-3'),
        t('ingredients-group-item-4'),
        t('ingredients-group-item-5'),
        t('ingredients-group-item-6'),
        t('ingredients-group-item-7')
      ]
    },
    {
      name: t('ingredients-group-name-2'),
      image: MineralsImage,
      items: [
        t('ingredients-group-item-8'),
        t('ingredients-group-item-9'),
        t('ingredients-group-item-10'),
        t('ingredients-group-item-11'),
        t('ingredients-group-item-12'),
        t('ingredients-group-item-13')
      ]
    },
    {
      name: t('ingredients-group-name-3'),
      image: LiverImage,
      items: [
        t('ingredients-group-item-14'),
        t('ingredients-group-item-15'),
        t('ingredients-group-item-16'),
        t('ingredients-group-item-17'),
        t('ingredients-group-item-18'),
        t('ingredients-group-item-19')
      ]
    },
    {
      name: t('ingredients-group-name-4'),
      image: VitDImage,
      items: [
        t('ingredients-group-item-20'),
        t('ingredients-group-item-21'),
        t('ingredients-group-item-22'),
        t('ingredients-group-item-23')
      ]
    },
    {
      name: t('ingredients-group-name-5'),
      image: OmegaImage,
      items: [
        t('ingredients-group-item-24'),
        t('ingredients-group-item-25'),
        t('ingredients-group-item-26')
      ]
    }
  ];

  const router = useRouter();

  const ab = router.locale === 'pl' && router.query['ab'];

  return (
    <>
      <section
        className={`${styles.section} ${styles.bedge} ${styles.firstSection}`}
      >
        <div className="container">
          <div className={`split ${styles.mobileReverse}`}>
            <div className={`half ${styles.firstHalf}`}>
              <div
                className={`${styles.firstHalfContainer} ${
                  ab ? styles.sectionBeforeAB : ''
                }`}
              >
                <h2>{t('main-section-header')}</h2>
                <p>{t('main-section-description')}</p>

                <CtaButton text={t('main-section-cta-button')} />
              </div>
            </div>
            <div className={`half ${styles.secondHalf}`}>
              <Image
                src={MainImage}
                alt="Main Image"
                width={651}
                height={614}
              />
            </div>
          </div>
        </div>
      </section>

      <div
        className={`${styles.questionContainer} ${ab ? styles.sectionAB : ''}`}
      >
        <Image src={SurveyProgressIcon} alt="Survey progress" />

        <h3>Czy wiesz, jakie masz niedobory?</h3>

        <div className={styles.surveyButtonsContainer}>
          <button
            onClick={() => {

              pushToDataLayer('test_event', {
                event_name: 'survey_first_question_clicked',
                action: 'yes'
              });
              // @ts-ignore
              window.location.href = process.env.NEXT_PUBLIC_SURVEY_URL;
            }}
            className={styles.surveyButton}
          >
            Tak
          </button>
          <button
            onClick={() => {
              pushToDataLayer('test_event', {
                event_name: 'survey_first_question_clicked',
                action: 'no'
              });
              // @ts-ignore
              window.location.href = process.env.NEXT_PUBLIC_SURVEY_URL;
            }}
            className={styles.surveyButton}
          >
            Nie
          </button>
        </div>
      </div>
      <section
        className={`${styles.section} ${styles.noMobilePadding} ${
          ab ? styles.sectionAfterAB : ''
        }`}
        style={{ borderBottom: '1px solid #E8E8E8' }}
      >
        <div className="container">
          <div className={`split ${styles.noFlexWrap} ${styles.fullWidth}`}>
            <div className={`${'half'} ${styles.summary}`}>
              <p>{t('review-first-text')}</p>
              <div className={styles.trustpilotBox}>
                <Image
                  src={Trustpilot2}
                  alt="Trustpilot Image"
                  width={144}
                  height={52}
                />
                <p className={styles.reviewText}>{t('trustpilot-rating')}</p>
              </div>
            </div>
            <div className={`${'half'} ${styles.summary}`}>
              <p>{t('review-second-text')}</p>

              <div className={styles.ingredientsQuality}>
                <Image
                  layout="fixed"
                  src={VeganIcon}
                  alt="Icon"
                  width={32}
                  height={39}
                />
                <Image
                  layout="fixed"
                  src={IfsIcon}
                  alt="Icon"
                  width={58}
                  height={34}
                />
                <Image
                  layout="fixed"
                  src={BrgsIcon}
                  alt="Icon"
                  width={45}
                  height={34}
                />
                <div className={styles.mobileNone}>
                  <Image
                    layout="fixed"
                    src={FscIcon}
                    alt="Icon"
                    width={35}
                    height={40}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.withPadding}`}>
        <div className="container">
          <h2 className={styles.headerShort}>{t('why-header')}</h2>
          <div className={`split ${styles.needsContainer}`}>
            <div className={'half'}>
              <h2>{t('why-inside-first-header')}</h2>
              <p>{t('why-inside-first-description')}</p>

              <div className={`split ${styles.needsPoints}`}>
                <div className={`${'alwaysHalf'}`}>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon1} alt="Icon" />
                    <p>{t('why-inside-first-list-item-1')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon3} alt="Icon" />
                    <p>{t('why-inside-first-list-item-2')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon5} alt="Icon" />
                    <p>{t('why-inside-first-list-item-3')}</p>
                  </div>
                </div>
                <div className={'alwaysHalf'}>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon2} alt="Icon" />
                    <p>{t('why-inside-first-list-item-4')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon4} alt="Icon" />
                    <p>{t('why-inside-first-list-item-5')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={NeedsIcon6} alt="Icon" />
                    <p>{t('why-inside-first-list-item-6')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={'half'}>
              <Image
                src={WhySundose1}
                alt="Main Image 1"
                className={styles.whyImage}
                width={585}
                height={546}
              />
            </div>
          </div>
          <div className={`split ${styles.needsContainer}`}>
            <div className={'half'}>
              <Image
                src={WhySundose2}
                alt="Main Image 2"
                className={styles.whyImage}
                width={585}
                height={546}
              />
            </div>
            <div className={`${'half'} ${styles.needsRightItem}`}>
              <h2>{t('why-inside-second-header')}</h2>
              <p>{t('why-inside-second-description')}</p>

              <div className={`split ${styles.needsPoints}`}>
                <div className={'alwaysHalf'}>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon1} alt="Icon" />
                    <p>{t('why-inside-second-list-item-1')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon3} alt="Icon" />
                    <p>{t('why-inside-second-list-item-2')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon5} alt="Icon" />
                    <p>{t('why-inside-second-list-item-3')}</p>
                  </div>
                </div>
                <div className={'alwaysHalf'}>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon2} alt="Icon" />
                    <p>{t('why-inside-second-list-item-4')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon4} alt="Icon" />
                    <p>{t('why-inside-second-list-item-5')}</p>
                  </div>
                  <div className={styles.iconWithText}>
                    <Image src={FittedIcon6} alt="Icon" />
                    <p>{t('why-inside-second-list-item-6')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.bedge} ${styles.withPadding}`}
      >
        <div className="container">
          <h2>{t('best-form-header')}</h2>
          <div className={`split ${styles.benefitIcons}`}>
            <div
              className={`${'quarter'} ${styles.iconWithTextUnder} ${
                styles.narrowText
              }`}
            >
              <Image src={BestFormIcon1} alt="Main Image" />
              <p>{t('best-form-benefit-1')}</p>
            </div>
            <div
              className={`${'quarter'} ${styles.iconWithTextUnder} ${
                styles.narrowText
              }`}
            >
              <Image src={BestFormIcon2} alt="Main Image" />
              <p>{t('best-form-benefit-2')}</p>
            </div>
            <div
              className={`${'quarter'} ${styles.iconWithTextUnder} ${
                styles.narrowText
              }`}
            >
              <Image src={BestFormIcon3} alt="Main Image" />
              <p>{t('best-form-benefit-3')}</p>
            </div>
            <div
              className={`${'quarter'} ${styles.iconWithTextUnder} ${
                styles.narrowText
              }`}
            >
              <Image src={BestFormIcon4} alt="Main Image" />
              <p>{t('best-form-benefit-4')}</p>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.blue} ${styles.withPadding}`}
      >
        <div className="container">
          <h2>{t('cta-first-section-header')}</h2>
          <CtaButton text={t('main-section-cta-button')} color="inverted" />
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.withPadding} ${styles.lessBottomPadding} ${styles.noMobilePadding}`}
      >
        <div className="container">
          <h2 className={styles.headerWithMobilePadding}>
            {t('ingredients-header')}
          </h2>
          <p className={styles.ingredientsParagraph}>
            {t('ingredients-description')}
          </p>
          <div className={styles.carouselWrapper}>
            <div className={styles.carouselContainer}>
              {ingredients.map(item => (
                <div
                  className={`${styles.carouselItem} ${styles.ingredient}`}
                  key={item.name}
                >
                  <Image
                    layout="fixed"
                    src={item.image}
                    alt={item.name}
                    width={52}
                    height={52}
                  />
                  <span>{item.name}</span>
                  {item.items.map(i => (
                    <p key={i}>{i}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.bedge} split`}
        style={{ minHeight: 240 }}
      >
        <div className={`container ${styles.infoIcons}`}>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon1} alt="Main Image" />
            <p>{t('benefit-1')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon2} alt="Main Image" />
            <p>{t('benefit-2')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon3} alt="Main Image" />
            <p>{t('benefit-3')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon4} alt="Main Image" />
            <p>{t('benefit-4')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon5} alt="Main Image" />
            <p>{t('benefit-5')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon6} alt="Main Image" />
            <p>{t('benefit-6')}</p>
          </div>
          <div className={`${styles.iconWithTextUnder} ${styles.scalableIcon}`}>
            <Image src={InfoIcon7} alt="Main Image" />
            <p>{t('benefit-7')}</p>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.withPadding} ${styles.borderBottom}`}
      >
        <div className={`container ${styles.researchContainer}`}>
          <h2 className={styles.headerShort}>{t('science-header')}</h2>
          <h3 className={styles.researchNumber}>{t('science-publications')}</h3>
          <h4 className={styles.researchSummary}>
            {t('science-publications-description')}
          </h4>

          <div className={'split'}>
            <div className={`${styles.iconWithTextUnder} ${styles.scientist}`}>
              <Image
                src={Adam}
                alt="Adam Kuzdraliński"
                layout={'fixed'}
                width={84}
                height={84}
              />
              <p className={styles.researchName}>dr hab. Adam Kuzdraliński</p>
              <p className={styles.researchPosition}>{t('job-title-1')}</p>
            </div>
            <div className={`${styles.iconWithTextUnder} ${styles.scientist}`}>
              <Image
                src={Monika}
                alt="Monika Maślej"
                layout={'fixed'}
                width={84}
                height={84}
              />
              <p className={styles.researchName}>Monika Maślej</p>
              <p className={styles.researchPosition}>{t('job-title-2')}</p>
            </div>
            <div className={`${styles.iconWithTextUnder} ${styles.scientist}`}>
              <Image
                src={Adrian}
                alt="Adrian Pawłowski"
                layout={'fixed'}
                width={84}
                height={84}
              />
              <p className={styles.researchName}>Adrian Pawłowski</p>
              <p className={styles.researchPosition}>{t('job-title-3')}</p>
            </div>
            <div
              className={`${styles.iconWithTextUnder} ${styles.scientist} ${styles.mobileNone}`}
            >
              <Image
                src={Agnieszka}
                alt="Agnieszka Gaczkowska"
                layout={'fixed'}
                width={84}
                height={84}
              />
              <p className={styles.researchName}>Agnieszka Gaczkowska</p>
              <p className={styles.researchPosition}>{t('job-title-4')}</p>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.withPadding} ${styles.lessBottomPadding} ${styles.noMobilePadding}`}
      >
        <div className="container">
          <h2 className={styles.headerWithMobilePadding}>
            {t('trustpilot-header')}
          </h2>
          <div className={styles.trustpilotDiv}>
            <Image
              src={Trustpilot}
              alt="Trustpilot Image"
              layout={'fixed'}
              width={260}
              height={43}
            />
            <p>{t('trustpilot-rating')}</p>
          </div>

          <div className={styles.carouselWrapper}>
            <div className={styles.carouselContainer}>
              {reviews.map(review => (
                <div
                  className={`${styles.carouselItem} ${styles.review}`}
                  key={review.name}
                >
                  <div className={styles.reviewHeader}>
                    <Image src={review.icon} alt="Review icon" />
                    <div className={styles.reviewPerson}>
                      <span>{review.name}</span>
                      <Image
                        src={Trustpilot3}
                        layout={'fixed'}
                        width={108}
                        height={27}
                        alt="Trustpilot review"
                      />
                    </div>
                  </div>
                  <p>{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.blue} ${styles.secondBlue} ${styles.withPadding}`}
      >
        <div className="container">
          <h2>{t('cta-second-section-header')}</h2>
          <CtaButton text={t('main-section-cta-button')} color="inverted" />
        </div>
      </section>
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    // @ts-ignore
    ...(await serverSideTranslations(locale, ['index', 'common']))
  }
});

export default Index;
